/**
 * Route Guard Types
 *
 * Type definitions for route protection middleware
 */

import type { ReactNode } from "react"

/**
 * User roles for role-based access control
 */
export type UserRole = "admin" | "manager" | "user" | "guest" | "IT_ADMIN" | (string & {})

/**
 * Route access types
 */
export type RouteAccess =
  | "public" // Anyone can access (landing, pricing, etc.)
  | "guest" // Only non-authenticated users (login, register)
  | "auth" // Only authenticated users (dashboard, profile)
  | "role" // Role-based access (admin panel, etc.)

/**
 * Base guard props
 */
export interface BaseGuardProps {
  children: ReactNode
  /** Custom loading component */
  fallback?: ReactNode
}

/**
 * Auth guard props (protected routes)
 */
export interface AuthGuardProps extends BaseGuardProps {
  /** Redirect path when not authenticated */
  redirectTo?: string
}

/**
 * Guest guard props (login, register pages)
 */
export interface GuestGuardProps extends BaseGuardProps {
  /** Redirect path when already authenticated */
  redirectTo?: string
}

/**
 * Role guard props
 */
export interface RoleGuardProps extends BaseGuardProps {
  /** Required roles (user must have at least one) */
  allowedRoles: UserRole[]
  /** Redirect path when role not allowed */
  redirectTo?: string
}

/**
 * Combined route guard props
 */
export interface RouteGuardProps extends BaseGuardProps {
  /** Type of route access */
  access: RouteAccess
  /** Required roles (only for 'role' access type) */
  allowedRoles?: UserRole[]
  /** Custom redirect path */
  redirectTo?: string
}

/**
 * Route configuration for declarative routing
 */
export interface RouteConfig {
  path: string
  element: ReactNode
  access?: RouteAccess
  allowedRoles?: UserRole[]
  layout?: "dashboard" | "plain" | "none"
  children?: RouteConfig[]
}

/**
 * Default routes
 */
export const DEFAULT_ROUTES = {
  /** Where to redirect unauthenticated users */
  signIn: "/",
  /** Where to redirect authenticated guests (from login page) — rol bazlı dağıtım için root */
  home: "/",
  /** Where to redirect unauthorized users (wrong role) */
  unauthorized: "/unauthorized-401",
  /** 404 page */
  notFound: "/not-found-404",
} as const
